import * as React from "react";
import { toast } from "react-toastify";

import { PostMediaFile } from "../../../../shared/types/post";
import { useTrackEvent } from "../../../../libs/analytics";

import { convertCollectionFileToPostMediaFile } from "../../convert-collection-file-to-post-media-file";

import { SelectedMediaFiles } from "../selected-media-files";
import { SelectedAssetsSidebar } from "./selected-assets-sidebar";

import {
  PICKER_ADD_ASSETS,
  SELECTED_MEDIA_FROM_LIBRARY,
} from "../../../../constants/Events";
import { LibraryPicker } from "./library/library-picker";
import { HashtagPicker } from "./hashtag/hashtag-picker";
import { CollectionFile } from "../../../../shared/types/files";
import { TaggedInPicker } from "./tagged-in/tagged-in-picker";
import { CreatorPicker } from "./creator/creator-picker";
import { StocksPicker } from "./stocks/stocks-picker";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedIgAccount } from "../../../../selectors/hashtagFeedSelectors";
import { AccountType } from "../../../../shared/types/accounts";
import { useInitMediaFeed } from "./components/account-selector";
import { isLibraryRestricted } from "../..";
import { CollectionSelector } from "../collection-selector/collection-selector";
import {
  getCollections,
  getIsLoadingCollections,
} from "../../../../selectors/mediaLibrarySelectors";
import { useState } from "react";
import {
  addCollectionAssetsFromFeed,
  addCollectionAssetsFromStock,
  loadCollections,
} from "../../../../actions/mediaLibrary";
import { ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE } from "../../../../constants/ActionTypes";
import {
  getRoutinesLoading,
  getUser,
} from "../../../../selectors/commonSelectors";
import { ConfirmRightsAlert } from "./components/confirm-rights-alert";
import { useDisclaimer } from "../../../../components/MediaLibrary/SourceMedia/Disclaimer/Disclaimer";
import {
  convertFileToFeed,
  convertFileToStockImage,
  getFileFormat,
} from "./helper";
import { getAccountsForPlatform } from "../../../../selectors/skedCoreSelectors";
import { selectIgAccount } from "../../../../actions/sourceMedia";
import { SMSplashHashtagContent } from "../sm-splashes/sm-splash-hashtag";
import { SMSplashTaggedInContent } from "../sm-splashes/sm-splash-tagged-in";
import { SMSplashCreatorsContent } from "../sm-splashes/sm-splash-creators";
import { MediaCollection } from "../../../../api/media-library";
import { cn } from "../../../../lib/utils";
import type { ReduxRootState } from "../../../../types/redux-store";
import { insertSelectedFile } from "./insert-selected-file";

export type MediaPickerTab = keyof typeof tabLabelMappings;

const isMediaPickerTab = (tab: string): tab is MediaPickerTab =>
  Object.hasOwn(tabLabelMappings, tab);

export function useInitCollections() {
  const dispatch = useDispatch<any>();
  const collections = useSelector(getCollections);
  const isLoadingCollections = useSelector(getIsLoadingCollections);
  const isFetching = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE])
  );

  React.useEffect(() => {
    if (!isLoadingCollections && collections.length === 0) {
      dispatch(loadCollections());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections.length, isLoadingCollections]);

  return {
    isLoadingCollections,
    isFetching,
    collections,
  };
}

export interface MediaPickerProps {
  onSelect(files: PostMediaFile[]): Promise<void>;
  limit: number;
  /** Most assets one post can hold; drives the sidebar counter only. */
  postLimit?: number;
  onClose(): void;
  selectedAccountIds: string[];
  renderCloseButton?: () => React.ReactNode;
  initialTab?: MediaPickerTab;
  renderActionButtons?(files: CollectionFile[]): React.ReactNode;
  clearOnClose?: boolean;
  closeRef?: React.MutableRefObject<{
    close?: () => void;
  }>;
  persistState?: boolean;
  onSkip?: () => void;
  hideCloseButton?: boolean;
  hideFooter?: boolean;
  footerClassName?: string;
  submitButtonTitle?: string;
  showBulkActions?: boolean;
  enforceCarouselLimit?: boolean;
  onSelectionChange?: (files: CollectionFile[]) => void;
  footerRef?: React.MutableRefObject<{
    handleSubmit?: () => void;
    handleBulkStories?: () => void;
    handleFileRemove?: (file: CollectionFile) => void;
    handleFilesClear?: () => void;
  }>;
}

const tabLabelMappings = {
  library: "Library",
  hashtag: "Hashtags",
  "tagged-in": "Tagged In",
  creator: "Influencers",
  stocks: "Stock Images",
};

const STORAGE_KEY = "sked:mediaSelectModal:state";

export function MediaPicker(props: MediaPickerProps) {
  const dispatch = useDispatch();
  const [selectedMediaFiles, setSelectedMediaFiles] = React.useState<
    Map<string, CollectionFile>
  >(new Map());
  const selectedMediaFilesRef = React.useRef(selectedMediaFiles);
  const [currentTab, setCurrentTab] = useState<MediaPickerTab>(
    props.initialTab || "library"
  );
  const [initializedTabs, setInitializedTabs] = useState<string[]>([]);

  const trackEvent = useTrackEvent();
  const currentUser = useSelector(getUser);
  const isUserRestricted = isLibraryRestricted(currentUser);
  const initMediaFeed = useInitMediaFeed();
  const { collections } = useInitCollections();
  const [selectedCollection, setSelectedCollection] =
    useState<MediaCollection | null>(null);
  const [, setShowConfirmModal] = useState(false);
  const hasRestoredState = React.useRef(false);

  React.useEffect(() => {
    selectedMediaFilesRef.current = selectedMediaFiles;
  }, [selectedMediaFiles]);

  // Restore state from localStorage if persistState is enabled
  React.useEffect(() => {
    if (!props.persistState) return;
    if (hasRestoredState.current) return; // Only restore once

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const { selectedCollectionId, currentTab: savedTab } =
          JSON.parse(stored);

        // Restore tab if not overridden by initialTab prop
        if (!props.initialTab && savedTab && isMediaPickerTab(savedTab)) {
          setCurrentTab(savedTab);
        }

        // Restore collection after collections are loaded
        if (selectedCollectionId && collections.length > 0) {
          const collection = collections.find(
            (c) => c._id === selectedCollectionId
          );
          if (collection) {
            setSelectedCollection(collection);
            hasRestoredState.current = true;
            return; // Skip default selection if we restored
          }
        }
      }
    } catch (e) {
      console.warn("Failed to restore media select state", e);
    }

    // If no stored collection and collections are loaded, default to first
    if (!selectedCollection && collections.length > 0) {
      setSelectedCollection(collections[0]);
      hasRestoredState.current = true;
    }
  }, [props.persistState, props.initialTab, collections]);

  // Save state when it changes (only if persistState is enabled)
  React.useEffect(() => {
    if (!props.persistState) return;

    const state = {
      selectedCollectionId: selectedCollection?._id || null,
      currentTab,
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn("Failed to save media select state", e);
    }
  }, [props.persistState, selectedCollection, currentTab]);
  const { onUpdate, disclaimerOpen } = useDisclaimer({});
  const selectedIgAccount = useSelector(getSelectedIgAccount);
  const accounts: AccountType[] = useSelector((state: ReduxRootState) =>
    getAccountsForPlatform(state, "IG")
  );

  if (props.closeRef?.current) {
    props.closeRef.current.close = () => {
      if (props.clearOnClose) {
        handleFilesClear();
      }
      props.onClose();
    };
  }

  if (props.footerRef?.current) {
    props.footerRef.current.handleSubmit = () => handleSubmit(disclaimerOpen);
    props.footerRef.current.handleBulkStories = handleBulkStories;
    props.footerRef.current.handleFileRemove = handleFileRemove;
    props.footerRef.current.handleFilesClear = handleFilesClear;
  }

  React.useEffect(() => {
    // Only reset if this is the FIRST account selection (initial load)
    if (selectedIgAccount && initializedTabs.length === 0) {
      setInitializedTabs([props.initialTab || "library"]);
    }
  }, [selectedIgAccount, props.initialTab]); // Don't depend on currentTab to avoid interference

  /** Which caller selection we have already adopted, so we adopt it once. */
  const adoptedCallerKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    // 1. we need to select the first ig account by default
    // 2. if user selects multiple ig accounts when creating the post
    // we should select the first selected ig account instead.
    const postSelectedAccounts = props.selectedAccountIds
      .map((id) => accounts.find((acc) => acc._id === id))
      .filter(Boolean);

    const selectedAccounts =
      postSelectedAccounts.length > 0 ? postSelectedAccounts : accounts;

    if (isUserRestricted || selectedAccounts.length === 0) {
      return;
    }

    const wanted = selectedAccounts[0]?._id;
    if (!wanted) {
      return;
    }

    // `selectedIgAccount` persists in the store between opens, so the picker
    // would otherwise keep showing whichever account was chosen last rather
    // than the one the caller opened it for. Selection performs no request, so
    // adopting the caller's account is free.
    //
    // ONCE per caller selection, though. This effect re-runs when the store
    // selection changes, and the feeds' own AccountSelector changes it, so
    // re-adopting unconditionally would revert the user's choice the instant
    // they made it, and the reconnect notice tells them to switch account,
    // which would then be impossible.
    const callerKey = props.selectedAccountIds.join(",");
    const callerNamedAnAccount = postSelectedAccounts.length > 0;
    if (!selectedIgAccount) {
      // Only claim the caller's selection as adopted if it was actually
      // resolved. `accounts` can arrive after this first run, in which case
      // `wanted` is a fallback, and stamping the key here would make the
      // branch below skip the real adoption once the account does land.
      if (callerNamedAnAccount) {
        adoptedCallerKeyRef.current = callerKey;
      }
      dispatch(selectIgAccount(wanted));
      return;
    }

    if (callerNamedAnAccount && adoptedCallerKeyRef.current !== callerKey) {
      adoptedCallerKeyRef.current = callerKey;
      if (selectedIgAccount._id !== wanted) {
        dispatch(selectIgAccount(wanted));
      }
    }
  }, [selectedIgAccount, isUserRestricted, accounts, props.selectedAccountIds]);

  const tabs = [
    "library",
    "hashtag",
    "tagged-in",
    "creator",
    "stocks",
  ] as const;

  const saveFiles = (): Promise<Map<string, CollectionFile>> => {
    if (!selectedCollection || filesToSave.length === 0) {
      return Promise.resolve(selectedMediaFiles);
    }

    const stockImages = filesToSave.filter((file) => file.picker === "stocks");
    const feedImages = filesToSave.filter((file) => file.picker !== "stocks");

    const addStockImages =
      stockImages.length > 0
        ? new Promise((resolve) => {
            (selectedCollection.id,
              dispatch(
                addCollectionAssetsFromStock({
                  collectionId: selectedCollection.id,
                  stockItems: stockImages.map(convertFileToStockImage),
                  location: "picker",
                  done: resolve,
                })
              ));
          })
        : Promise.resolve();

    const addFeedImages =
      feedImages.length > 0
        ? new Promise((resolve) => {
            (selectedCollection.id,
              dispatch(
                addCollectionAssetsFromFeed({
                  collectionId: selectedCollection.id,
                  feedItems: feedImages.map(convertFileToFeed),
                  location: "picker",
                  done: resolve,
                })
              ));
          })
        : Promise.resolve();

    return Promise.all([addStockImages, addFeedImages])
      .then((results) => {
        const [stockImagesResults = [], feedImagesResults = []] = results as [
          CollectionFile[],
          CollectionFile[],
        ];

        // Validate results
        const allResults = [...stockImagesResults, ...feedImagesResults];

        if (allResults.length === 0 && filesToSave.length > 0) {
          const error = new Error("Save to collection returned no results");
          console.error("Save validation failed:", {
            filesRequested: filesToSave.length,
            filesReturned: allResults.length,
            collectionId: selectedCollection?.id,
            collectionName: selectedCollection?.name,
          });
          throw error;
        }

        // Check for incomplete assets
        const invalidAssets = allResults.filter(
          (asset) => !asset._id || !asset.asset_id
        );

        if (invalidAssets.length > 0) {
          console.error("Invalid assets returned from save:", {
            count: invalidAssets.length,
            total: allResults.length,
            invalid: invalidAssets.map((a) => ({
              _id: a._id,
              asset_id: a.asset_id,
              localId: a.localId,
              name: a.name,
            })),
          });
          throw new Error(
            `${invalidAssets.length} of ${allResults.length} assets saved without required fields`
          );
        }

        // Update selectedMediaFiles Map with new asset_id values from library
        const updatedMap = new Map(selectedMediaFiles);
        let matchedCount = 0;
        const unmatchedAssets: CollectionFile[] = [];

        for (const imageAsset of allResults) {
          const localAssetId = imageAsset?.localId;

          if (!localAssetId || !imageAsset.asset_id) {
            console.error("Incomplete asset data from save:", {
              localId: localAssetId,
              asset_id: imageAsset.asset_id,
              imageAsset,
            });
            unmatchedAssets.push(imageAsset);
            continue;
          }

          // Find the entry in the map by matching localId
          // Map is keyed by file._id, but we need to match by localId
          let mapKey = null;
          let selectedMediaFile = null;

          for (const [key, value] of updatedMap.entries()) {
            // Match by either _id or localId (external media might use either)
            if (
              value._id === localAssetId ||
              value.localId === localAssetId ||
              value.id === localAssetId
            ) {
              mapKey = key;
              selectedMediaFile = value;
              matchedCount++;
              break;
            }
          }

          if (!selectedMediaFile || !mapKey) {
            console.error("Failed to match saved asset to selected file:", {
              localAssetId,
              imageAsset,
              mapKeys: Array.from(updatedMap.keys()),
              mapValues: Array.from(updatedMap.values()).map((v) => ({
                _id: v._id,
                localId: v.localId,
                id: v.id,
                picker: v.picker,
              })),
            });
            unmatchedAssets.push(imageAsset);
            continue;
          }

          // Update using the ORIGINAL map key
          // Use full library asset to get S3 URLs instead of external CDN URLs
          updatedMap.set(mapKey, {
            ...imageAsset, // Use full library asset (has S3 URLs)
            picker: selectedMediaFile.picker, // Preserve original source for tracking
            localId: selectedMediaFile.localId, // Preserve for matching
          });
        }

        // Verify all files were matched
        if (unmatchedAssets.length > 0) {
          console.error("Failed to match some assets:", {
            unmatchedCount: unmatchedAssets.length,
            totalSaved: allResults.length,
            matched: matchedCount,
            unmatched: unmatchedAssets.map((a) => ({
              _id: a._id,
              localId: a.localId,
              name: a.name,
            })),
          });
          throw new Error(
            `Failed to match ${unmatchedAssets.length} of ${allResults.length} saved assets`
          );
        }

        // Trigger React state update so changes are reflected
        selectedMediaFilesRef.current = updatedMap;
        setSelectedMediaFiles(updatedMap);

        trackEvent({
          eventName: PICKER_ADD_ASSETS,
          metadata: {
            total: filesToSave.length,
          },
        });

        // Show success toast
        const count = filesToSave.length;
        const collectionName = selectedCollection?.name || "collection";
        toast.success(
          `Saved ${count} ${count === 1 ? "asset" : "assets"} to ${collectionName}`
        );

        // Return updated map for immediate use
        return updatedMap;
      })
      .catch((error) => {
        // Ensure errors are properly thrown
        console.error("saveFiles error:", error);
        throw error; // Re-throw so handleSubmit can catch it
      });
  };

  const filesToSave = React.useMemo(
    () =>
      [...selectedMediaFiles.values()].filter(
        (file) => file.picker !== "library"
      ),
    [selectedMediaFiles]
  );

  async function handleSubmit(disclaimerOpen: boolean) {
    const filesNeededConfirmRights = filesToSave.filter(
      (file) => !["library", "stocks"].includes(file.picker || "")
    );

    if (disclaimerOpen && filesNeededConfirmRights.length > 0) {
      setShowConfirmModal(true);
      return;
    }

    trackEvent({
      eventName: SELECTED_MEDIA_FROM_LIBRARY,
      metadata: {
        length: selectedMediaFiles.size,
      },
    });

    // Save external files to library and get updated map with asset_id values
    let finalMediaFiles = selectedMediaFiles;
    if (filesToSave.length > 0) {
      try {
        finalMediaFiles = await saveFiles();

        // Verify all external files have asset_id after save
        const missingAssetIds = [...finalMediaFiles.values()].filter(
          (file) => file.picker !== "library" && !file.asset_id
        );

        if (missingAssetIds.length > 0) {
          console.error("Some files missing asset_id after save:", {
            count: missingAssetIds.length,
            files: missingAssetIds.map((f) => ({
              _id: f._id,
              localId: f.localId,
              id: f.id,
              picker: f.picker,
              name: f.name,
            })),
          });
          toast.error(
            `Failed to save ${missingAssetIds.length} asset${missingAssetIds.length === 1 ? "" : "s"} to collection. Please try again.`
          );
          return; // Keep modal open, don't proceed
        }
      } catch (error) {
        console.error("Save to collection failed:", error);
        toast.error(
          "Failed to save assets to collection. " +
            (error instanceof Error ? error.message : "Please try again.")
        );
        return; // Keep modal open, don't proceed
      }
    }

    // Wrap post creation in try/catch as well
    try {
      await props.onSelect(
        [...finalMediaFiles.values()].map(convertCollectionFileToPostMediaFile)
      );
      if (props.clearOnClose) {
        handleFilesClear();
      }
    } catch (error) {
      console.error("Post creation failed:", error);
      toast.error(
        "Failed to create posts. " +
          (error instanceof Error ? error.message : "Please try again.")
      );
      // Modal stays open so user can retry
    }
  }

  function handleFileAdd(file: CollectionFile, placedAfter?: number) {
    const currentSelection = selectedMediaFilesRef.current;

    if (currentSelection.has(file._id)) {
      return;
    }

    if (currentSelection.size >= props.limit) {
      toast.error(`A maximum of ${props.limit} assets can be selected`);
      return;
    }

    const next = insertSelectedFile(
      currentSelection,
      file._id,
      { ...file, picker: currentTab },
      placedAfter
    );

    selectedMediaFilesRef.current = next;
    setSelectedMediaFiles(next);
    props.onSelectionChange?.([...next.values()]);
  }

  function handleFileRemove(file: CollectionFile) {
    const next = new Map(selectedMediaFilesRef.current);
    next.delete(file._id);
    selectedMediaFilesRef.current = next;
    setSelectedMediaFiles(next);
    props.onSelectionChange?.([...next.values()]);
  }

  function handleFilesClear() {
    const next = new Map<string, CollectionFile>();
    selectedMediaFilesRef.current = next;
    setSelectedMediaFiles(next);
    props.onSelectionChange?.([]);
  }

  async function handleBulkStories() {
    // Save external files to library first and get updated map
    let finalMediaFiles = selectedMediaFiles;
    if (filesToSave.length > 0) {
      try {
        finalMediaFiles = await saveFiles();

        // Verify all external files have asset_id after save
        const missingAssetIds = [...finalMediaFiles.values()].filter(
          (file) => file.picker !== "library" && !file.asset_id
        );

        if (missingAssetIds.length > 0) {
          console.error("Some files missing asset_id after save for stories:", {
            count: missingAssetIds.length,
            files: missingAssetIds,
          });
          toast.error(
            `Failed to save ${missingAssetIds.length} asset${missingAssetIds.length === 1 ? "" : "s"} to collection. Please try again.`
          );
          return; // Keep modal open
        }
      } catch (error) {
        console.error("Save to collection failed (bulk stories):", error);
        toast.error(
          "Failed to save assets to collection. " +
            (error instanceof Error ? error.message : "Please try again.")
        );
        return; // Keep modal open
      }
    }

    // Filter assets to story-compatible formats using updated map
    const storyCompatibleFormats = {
      image: ["png", "jpg", "jpeg"],
      video: ["mp4", "mov"],
    };

    const storyAssets = [...finalMediaFiles.values()].filter((file) => {
      const format = getFileFormat(file.meta);
      const mime = file.meta.mime?.toLowerCase() || "";

      // Check if it's an image with compatible format (check format first, then mime as fallback)
      if (file.meta.isImage) {
        if (storyCompatibleFormats.image.includes(format)) {
          return true;
        }
        // Fallback: check mime type (e.g. "image/jpeg", "image/png")
        if (
          mime.startsWith("image/") &&
          storyCompatibleFormats.image.includes(mime.replace("image/", ""))
        ) {
          return true;
        }
      }

      // Check if it's a video with compatible format and duration ≤60s
      if (file.meta.isVideo) {
        const duration = file.meta.duration || 0;
        if (duration > 60) {
          return false;
        }
        if (storyCompatibleFormats.video.includes(format)) {
          return true;
        }
        // Fallback: check mime type (e.g. "video/mp4", "video/quicktime" for .mov)
        if (
          mime.startsWith("video/") &&
          (storyCompatibleFormats.video.includes(mime.replace("video/", "")) ||
            mime === "video/quicktime")
        ) {
          return true;
        }
      }

      return false;
    });

    if (storyAssets.length === 0) {
      toast.error(
        "No story-compatible assets selected. Stories require images (PNG, JPG) or videos under 60 seconds (MP4, MOV)."
      );
      return;
    }

    // Show info if some assets were filtered out
    if (storyAssets.length < finalMediaFiles.size) {
      toast.info(
        `${storyAssets.length} of ${finalMediaFiles.size} assets are compatible with Instagram Stories. Incompatible assets have been filtered out.`
      );
    }

    trackEvent({
      eventName: SELECTED_MEDIA_FROM_LIBRARY,
      metadata: {
        length: storyAssets.length,
        type: "bulk-stories",
      },
    });

    // Pass filtered assets to parent with story metadata
    try {
      await props.onSelect(
        storyAssets.map((file) => ({
          ...convertCollectionFileToPostMediaFile(file),
          _isBulkStory: true, // Flag for parent to handle story mode
        }))
      );

      if (props.clearOnClose) {
        handleFilesClear();
      }
    } catch (error) {
      console.error("Bulk stories creation failed:", error);
      toast.error(
        "Failed to create story posts. " +
          (error instanceof Error ? error.message : "Please try again.")
      );
      // Modal stays open so user can retry
    }
  }

  function onTabChanged(tab: MediaPickerTab) {
    setCurrentTab(tab);
    if (selectedIgAccount && !initializedTabs.includes(tab)) {
      setInitializedTabs([...initializedTabs, tab]);

      if (
        tab === "tagged-in" &&
        !(selectedIgAccount.session?.scopes || []).includes(
          "instagram_manage_comments"
        )
      ) {
        return;
      }

      initMediaFeed(tab, selectedIgAccount);
    }
  }

  function onCollectionSelected(collectionId: string) {
    const targetCollection = collections.find(
      (colt) => colt._id === collectionId
    );

    if (targetCollection) {
      setSelectedCollection(targetCollection);
    }
  }

  // Create post button should be enabled when 0 assets (acts as skip) or 1-10 assets
  // Only disable when > limit or when files need to be saved but no collection selected
  const useMediaDisabled =
    selectedMediaFiles.size > props.limit ||
    (filesToSave.length > 0 && !selectedCollection);

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {/* New flex layout: main content + right sidebar */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Main content area */}
        <div className="flex min-h-0 flex-1 flex-col border-r border-neutral-200">
          <div className="px-6 pt-4 pb-3" data-testid="picker-tabs">
            <div className="flex gap-1 overflow-x-auto rounded-lg bg-neutral-50 p-1">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  data-testid={`picker-tab-${tab}`}
                  onClick={() => onTabChanged(tab)}
                  className={cn(
                    "flex-1 rounded-lg px-3 py-1.5 font-inter text-sm font-medium whitespace-nowrap transition-all",
                    tab === currentTab
                      ? "bg-white text-blue-500 shadow-sm"
                      : "bg-transparent text-neutral-700 hover:bg-neutral-25 hover:text-neutral-900"
                  )}
                >
                  {tabLabelMappings[tab]}
                </button>
              ))}
            </div>
          </div>

          {/* Collection selector alert - moved to top */}
          {currentTab !== "library" && filesToSave.length > 0 && (
            <div className="mx-6 mb-3 flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
              {disclaimerOpen && (
                <ConfirmRightsAlert
                  pickerType={currentTab}
                  onAccept={onUpdate}
                />
              )}
              <div
                className="ml-auto flex items-center gap-3"
                data-testid="collection-selector-wrapper"
              >
                <div className="flex items-center gap-2 text-blue-700">
                  <i className="fa-regular fa-circle-info"></i>
                  <span className="font-inter text-sm font-medium">
                    Save selected media to a collection
                  </span>
                </div>
                <CollectionSelector
                  selectedCollection={selectedCollection}
                  onChange={onCollectionSelected}
                  collections={collections}
                />
              </div>
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
            <PickerTabContent
              tab={currentTab}
              onClose={() => {
                if (props.clearOnClose) {
                  handleFilesClear();
                }
                props.onClose();
              }}
              limit={props.limit}
              handleFileAdd={handleFileAdd}
              handleFileRemove={handleFileRemove}
              selectedMediaFiles={selectedMediaFiles}
              selectedCollection={selectedCollection}
              isUserRestricted={isUserRestricted}
            />
          </div>
        </div>

        {/* Right sidebar for selected assets */}
        <SelectedAssetsSidebar
          selectedAssets={[...selectedMediaFiles.values()]}
          maxAssets={props.limit}
          postLimit={props.postLimit}
          onRemoveAsset={handleFileRemove}
        />
      </div>

      {/* Action buttons at bottom */}
      {!props.hideFooter && (
        <div className={cn("shrink-0", props.footerClassName)}>
          <SelectedMediaFiles
            limit={props.limit}
            selectedMediaFiles={[...selectedMediaFiles.values()]}
            onRemove={handleFileRemove}
            onClear={handleFilesClear}
            onCancel={props.onClose}
            onSubmit={() => handleSubmit(disclaimerOpen)}
            onBulkStories={handleBulkStories}
            onSkip={props.onSkip}
            buttonTitle={props.submitButtonTitle}
            showBulkActions={props.showBulkActions}
            enforceCarouselLimit={props.enforceCarouselLimit}
            disabled={useMediaDisabled}
            renderActionButtons={props.renderActionButtons}
          />
        </div>
      )}
    </div>
  );
}

export interface PickerProps {
  onClose(): void;
  limit: number;
  handleFileAdd: (file: CollectionFile, placedAfter?: number) => void;
  handleFileRemove: (file: CollectionFile) => void;
  selectedMediaFiles: Map<string, CollectionFile>;
  selectedCollection: MediaCollection | null;
  isUserRestricted: boolean;
}

export interface PickerTabContentProps extends PickerProps {
  tab: MediaPickerTab;
}

export function PickerTabContent(props: PickerTabContentProps) {
  if (props.tab === "library") {
    return <LibraryPicker {...props} />;
  }

  if (props.tab === "stocks") {
    return <StocksPicker {...props} />;
  }

  switch (props.tab) {
    case "hashtag": {
      return props.isUserRestricted ? (
        <div className="text-sm p-5 [&_li]:mb-2.5 [&_button]:text-xs [&_button]:px-5 [&_button]:mb-5 [&_p]:text-sm [&_p]:mb-0 [&_p_button]:text-sm [&_p_button]:pl-0">
          <SMSplashHashtagContent />
        </div>
      ) : (
        <HashtagPicker {...props} />
      );
    }

    case "tagged-in": {
      return props.isUserRestricted ? (
        <div className="text-sm p-5 [&_li]:mb-2.5 [&_button]:text-xs [&_button]:px-5 [&_button]:mb-5 [&_p]:text-sm [&_p]:mb-0 [&_p_button]:text-sm [&_p_button]:pl-0">
          <SMSplashTaggedInContent />
        </div>
      ) : (
        <TaggedInPicker {...props} />
      );
    }

    case "creator": {
      return props.isUserRestricted ? (
        <div className="text-sm p-5 [&_li]:mb-2.5 [&_button]:text-xs [&_button]:px-5 [&_button]:mb-5 [&_p]:text-sm [&_p]:mb-0 [&_p_button]:text-sm [&_p_button]:pl-0">
          <SMSplashCreatorsContent />
        </div>
      ) : (
        <CreatorPicker {...props} />
      );
    }

    default: {
      return <div className="min-h-[300px]" />;
    }
  }
}
